import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { DISCORD_URL } from '../../config';
import styles from './Navbar.module.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Story', to: '/story' },
  { label: 'Races', to: '/races' },
  { label: 'Download', to: '/download' },
  { label: 'FAQ', to: '/faq' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  // Scroll to top when clicking home/logo — even if already on the home page
  const handleHomeClick = useCallback(() => {
    closeMenu();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  return (
    <>
      <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
        <nav className={styles.nav} aria-label="Main navigation">
          <Link to="/" className={styles.logo} onClick={handleHomeClick}>
            <img src="/logo.svg" alt="L2 shield logo" className={styles.logoIcon} aria-hidden="true" />
            Lineage II Community
          </Link>

          <button
            className={`${styles.hamburger} ${menuOpen ? styles.open : ''}`}
            onClick={() => setMenuOpen(prev => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>

          {/* Desktop nav links rendered inside header */}
          <ul className={styles.navListDesktop}>
            {NAV_LINKS.map(({ label, to }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                  onClick={to === '/' ? handleHomeClick : undefined}
                >
                  {label}
                </NavLink>
              </li>
            ))}
            <li>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.navLink}
              >
                Discord
              </a>
            </li>
            <li>
              <NavLink
                to="/account"
                className={({ isActive }) => `${styles.accountBtn} ${isActive ? styles.accountBtnActive : ''}`}
              >
                Account
              </NavLink>
            </li>
          </ul>
        </nav>
      </header>

      {/* Mobile drawer — rendered outside header to escape backdrop-filter stacking context */}
      <div
        className={`${styles.overlay} ${menuOpen ? styles.overlayVisible : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
      <nav
        className={`${styles.drawer} ${menuOpen ? styles.drawerOpen : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
      >
        <ul className={styles.drawerList}>
          {NAV_LINKS.map(({ label, to }) => (
            <li key={to} className={styles.drawerItem}>
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) => `${styles.drawerLink} ${isActive ? styles.drawerLinkActive : ''}`}
                onClick={to === '/' ? handleHomeClick : closeMenu}
              >
                {label}
              </NavLink>
            </li>
          ))}
          <li className={styles.drawerItem}>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.drawerLink}
              onClick={closeMenu}
            >
              Discord
            </a>
          </li>
          <li className={`${styles.drawerItem} ${styles.drawerAccountItem}`}>
            <NavLink
              to="/account"
              className={({ isActive }) => `${styles.drawerAccountBtn} ${isActive ? styles.drawerAccountBtnActive : ''}`}
              onClick={closeMenu}
            >
              Account
            </NavLink>
          </li>
        </ul>
      </nav>
    </>
  );
}
